import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { tipoff } from "@/lib/format";
import "./StartingLineups.css";

const LABEL = { confirmed: "CONFIRMED", historical: "HISTORICAL", unavailable: "NOT AVAILABLE", expected: "EXPECTED" };
const SOURCE = { nba: "NBA.com", rotowire: "RotoWire", manual: "NBACUBE admin" };

function NoonFive({ noon, side }) {
  if (!noon) return null;
  const src = SOURCE[noon.source] || "expected";
  const when = noon.at ? new Date(noon.at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Athens" }) : "";
  const done = noon.matched != null;
  return (
    <div className={`sl-noon ${done ? (noon.matched === 5 ? "ok" : "diff") : ""}`} data-testid={`lineup-noon-${side}`}>
      <span className="sl-noon-title">Noon five · {src} {when}</span>
      {done ? (
        <span data-testid={`lineup-noon-matched-${side}`}>
          <b>{noon.matched}/5</b> matched
          {noon.dropped?.length > 0 && <> · out: {noon.dropped.join(", ")}</>}
          {noon.added?.length > 0 && <> · in: {noon.added.join(", ")}</>}
        </span>
      ) : (
        <span>{noon.starters.join(", ")}</span>
      )}
    </div>
  );
}

function MayNotPlay({ list, side }) {
  if (!list?.length) return null;
  return (
    <div className="sl-mnp" data-testid={`lineup-may-not-play-${side}`}>
      <span className="sl-mnp-title">May not play</span>
      {list.map((x) => (
        <span key={x.name} className={`sl-mnp-item ${x.status === "Out" ? "is-out" : ""}`}>{x.name}{x.status ? ` · ${x.status}` : ""}</span>
      ))}
    </div>
  );
}

function TeamColumn({ team, historical, gameId, filter, onTeam }) {
  const state = filter === "all" ? "" : filter === team.code ? "is-picked" : "is-dimmed";
  return (
    <div className={`sl-team ${state}`} data-testid={`lineup-team-${team.side}`} style={{ "--team-primary": team.primary_color, "--team-secondary": team.secondary_color }}>
      <button type="button" className="sl-team-header sl-team-btn" data-testid={`lineup-team-filter-${team.side}`} aria-pressed={filter === team.code}
        title={filter === team.code ? "Show both teams" : `Show only ${team.code} players`} onClick={() => onTeam(team.code)}>
        {team.logo && <img src={team.logo} alt={team.code} />}
        <div><strong>{team.code} ({team.side.toUpperCase()})</strong>{team.record && <small data-testid={`lineup-record-${team.side}`}>{team.record}</small>}</div>
        {team.score != null && <span className="sl-score" data-testid={`lineup-score-${team.side}`}>{team.score}</span>}
      </button>
      <div className="sl-roster">
        {team.starters.map((p) => (
          <Link key={p.player_id} to={`/player/${p.player_id}?game=${gameId}`} className="sl-player" data-testid={`starter-${p.player_id}`}>
            <span className="sl-number">{p.jersey ?? "—"}</span>
            <span className="sl-photo">{p.photo ? <img src={p.photo} alt={p.name} loading="lazy" /> : <span>NBA</span>}</span>
            <span className="sl-name">{p.name}</span>
            <span className="sl-role" data-testid={`starter-role-${p.player_id}`}>
              {historical ? (p.actual_pts == null ? "—" : `${p.actual_pts} PTS`) : p.position || "—"}
            </span>
          </Link>
        ))}
        {team.starters.length === 0 && <div className="sl-empty">Starting five not confirmed</div>}
      </div>
      {team.lineup_status && <div className={`sl-rw ${team.lineup_status}`} data-testid={`lineup-rw-status-${team.side}`}>{SOURCE[team.lineup_source] || "Expected"}: {team.lineup_status === "confirmed" ? "confirmed lineup" : "expected lineup"}</div>}
      <MayNotPlay list={team.may_not_play} side={team.side} />
      <NoonFive noon={team.noon} side={team.side} />
    </div>
  );
}

export function LineupPanel({ game, team = "all", onTeam }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    api.get(`/games/${game.id}/lineups`).then((r) => alive && setData(r.data)).catch(() => {});
    return () => { alive = false; };
  }, [game.id, game.lineups_announced]);

  if (!data) return <section className="sl-panel" data-testid="lineup-panel"><div className="sl-empty">Loading lineups…</div></section>;
  const historical = data.status === "historical";
  return (
    <section className="sl-panel" aria-label="Starting lineups" data-testid="lineup-panel">
      <div className="sl-title">
        <h2>Starting lineups</h2>
        <span className={`sl-status ${data.status}`} data-testid="lineup-status">{LABEL[data.status]}</span>
      </div>
      <div className="sl-teams">
        {data.teams.map((t) => <TeamColumn key={t.side} team={t} historical={historical} gameId={game.id} filter={team} onTeam={onTeam} />)}
      </div>
      {data.status === "expected" && (
        <p className="sl-note" data-testid="lineup-expected-note">
          Expected starters (NBACUBE admin five if set, else NBA.com, then RotoWire), used for the projections until ESPN confirms the official five (usually ~30 minutes before tip-off, {tipoff(game.date_utc)}).
        </p>
      )}
      {data.status === "unavailable" && (
        <p className="sl-note" data-testid="lineup-pending">
          {game.status === "pre"
            ? `ESPN usually confirms starters about 30 minutes before tip-off (${tipoff(game.date_utc)}). This page checks every 5 minutes.`
            : "No verified starting five stored for this game yet."}
        </p>
      )}
    </section>
  );
}
